import { useMediaQuery, useTheme, type MenuProps } from '@mui/material';
import { isEmbeddableWidgetBundle } from './embeddableWidgetPortalUtils';

/** Shape `Select` accepts for its `MenuProps`. */
type SelectMenuProps = Partial<Omit<MenuProps, 'children'>>;

const centredMenuProps: SelectMenuProps = {
  // Without an anchor reference the Popover leaves `top`/`left` unset, so the
  // paper is placed entirely by the styles below rather than against the
  // trigger. `embeddablePopoverPaperSx` (position: fixed) is overridden here
  // for the same reason.
  anchorReference: 'none',
  slotProps: {
    // Popover defaults its backdrop to invisible; a Dialog's is not.
    backdrop: { invisible: false },
    paper: {
      sx: {
        // Offsets resolve against the scoped modal root, which
        // `createEmbeddableWidgetTheme` sizes to the widget mount node. Pinning
        // the top rather than centring keeps a long option list inside the
        // widget: it opens a quarter of the way down and can grow to half the
        // container, leaving a quarter clear below. Overflowing lists scroll in
        // the paper instead of running past the widget.
        position: 'absolute',
        top: '25%',
        maxHeight: '50%',
        overflowY: 'auto',
        // Horizontal centring cannot use `transform`: the Grow transition
        // writes that inline on the paper and inline styles win. Auto margins
        // against a full-width inset centre it without touching `transform`.
        left: 0,
        right: 0,
        marginInline: 'auto',
        width: 'fit-content',
        maxWidth: '90%',
      },
    },
  },
};

/**
 * Gives a `Select` the presentation `EmbeddableMenu` and `EmbeddableGridPanel`
 * already give menus and grid panels in the widget bundle: centred in the mount
 * node over a backdrop covering only that container, holding its position while
 * the host page scrolls.
 *
 * Those two swap in a Dialog, which a `Select` cannot do because it owns its
 * dropdown. Reconfiguring the Popover it already renders reaches the same
 * place.
 *
 * Returns `undefined` outside the widget bundle and on real mobile viewports,
 * which leaves the Select's own anchored defaults untouched.
 */
export function useEmbeddableSelectMenuProps(): SelectMenuProps | undefined {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!isEmbeddableWidgetBundle || isMobile) {
    return undefined;
  }

  return centredMenuProps;
}
